import { Lead } from '@/types/lead';
import { formatCurrency } from '@/lib/utils';

/**
 * Export leads to CSV format
 * Converts an array of leads to a CSV string and triggers browser download
 */
export function exportLeadsToCSV(leads: Lead[], filename: string = 'leads-export.csv'): void {
  if (!leads || leads.length === 0) {
    console.warn('No leads to export');
    return;
  }

  // Define CSV headers
  const headers = [
    'Lead Name',
    'Company',
    'Contact Person',
    'Email',
    'Phone Number',
    'Industry',
    'Location',
    'Stage',
    'Priority',
    'Deal Value',
    'Expected Close Date',
    'Assigned To',
    'Days in Pipeline',
    'Days in Stage',
    'Last Activity',
    'Created At',
    'LinkedIn URL',
  ];

  // Convert leads to CSV rows
  const rows = leads.map(lead => [
    escapeCSVField(lead.name || ''),
    escapeCSVField(lead.company || ''),
    escapeCSVField(lead.contactPerson || ''),
    escapeCSVField(lead.email || ''),
    escapeCSVField(lead.phoneNumber || ''),
    escapeCSVField(lead.industry || ''),
    escapeCSVField(lead.location || ''),
    lead.stage,
    lead.priority,
    lead.dealValue ? lead.dealValue.toString() : '',
    lead.expectedClose ? new Date(lead.expectedClose).toLocaleDateString() : '',
    escapeCSVField(lead.assignedToUser?.name || ''),
    lead.daysInPipeline?.toString() || '',
    lead.daysInStage?.toString() || '',
    lead.lastActivity ? new Date(lead.lastActivity).toLocaleDateString() : '',
    new Date(lead.createdAt).toLocaleDateString(),
    escapeCSVField(lead.linkedinUrl || ''),
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Escape special characters in CSV fields
 * Wraps fields containing commas, quotes, or newlines in double quotes
 */
function escapeCSVField(field: string): string {
  if (!field) return '';

  // If field contains comma, quote, or newline, wrap in quotes and escape quotes
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }

  return field;
}

/**
 * Export selected leads to CSV with timestamp
 */
export function exportSelectedLeads(leads: Lead[]): void {
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `leads-export-${timestamp}.csv`;
  exportLeadsToCSV(leads, filename);
}
